import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import { assertUnreachable, createTranslatedText, TranslatableText, Translate } from '@dreryk/react-common';
import {
   appendTestableElementAttributes,
   basicBodyTextStyle,
   basicSmallTextStyle,
   StrongText,
   Text,
   TooltipText,
} from '@dreryk/react-components';

import { translationKeys } from './translations';
import {
   Practitioner,
   PractitionerIdentificationPresentation,
   PractitionerPresentationOrder,
   PractitionerSafePresenterRenderer,
   PractitionerSafePresenterRenderProps,
   PractitionerSafePresenterType,
} from './PractitionerSafePresenter.types';

export const StyledStandalonePractitionerSafePresenter = styled.div`
   display: flex;
   flex-direction: column;
`;

StyledStandalonePractitionerSafePresenter.displayName = 'StyledStandalonePractitionerSafePresenter';

export const StyledStandaloneDisplayNameText = styled(StrongText)`
   ${basicBodyTextStyle}
`;

StyledStandaloneDisplayNameText.displayName = 'StyledStandaloneDisplayNameText';

export const StyledStandaloneIdentificationText = styled(Text)`
   ${basicSmallTextStyle}
`;

StyledStandaloneIdentificationText.displayName = 'StyledStandaloneIdentificationText';

export const StyledStandaloneIdentificationTestAlarmText = styled(Text)`
   ${basicSmallTextStyle}
`;

StyledStandaloneIdentificationText.displayName = 'StyledStandaloneIdentificationText';

export const getPractitionerIdentification = (
   practitioner: Practitioner,
   sensitiveDataAvailable: boolean
): PractitionerIdentificationPresentation | undefined => {
   // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
   const maskSensitiveData = <T extends unknown>(data: T): string | T => {
      return sensitiveDataAvailable ? data : '***';
   };

   if (practitioner.medicalLicenceNumber) {
      return {
         type: translationKeys.MEDICAL_LICENCE_NUMBER,
         value: createTranslatedText(practitioner.medicalLicenceNumber.getValue()),
      };
   }
   if (practitioner.pesel) {
      return {
         type: translationKeys.PESEL,
         value: createTranslatedText(maskSensitiveData(practitioner.pesel.asString())),
      };
   }

   return undefined;
};

export const getPractitionerDisplayName = (
   practitioner: Practitioner,
   presentationOrder: PractitionerPresentationOrder | undefined
): TranslatableText | undefined => {
   if (practitioner.displayName) {
      return createTranslatedText(practitioner.displayName);
   }

   const formatDisplayName = (...names: ({ asString: () => string } | undefined)[]): TranslatableText => {
      return createTranslatedText(
         compact(names)
            .map((name) => name.asString())
            .join(' ')
      );
   };

   if (presentationOrder === 'lastName') {
      return formatDisplayName(practitioner.lastName, practitioner.firstName);
   }

   return formatDisplayName(practitioner.firstName, practitioner.lastName);
};

export const getPractitionerSpecialties = (practitioner: Practitioner): string[] | null | undefined => {
   if (isEmpty(practitioner.specialtyNames)) {
      return null;
   }

   return practitioner.specialtyNames;
};

export const selectPractitionerData = (
   practitioner: Practitioner,
   withSpecialties: boolean | undefined,
   sensitiveDataAvailable: boolean
): Pick<PractitionerSafePresenterRenderProps, 'identification' | 'specialties'> => {
   if (withSpecialties) {
      return { specialties: getPractitionerSpecialties(practitioner), identification: undefined };
   }

   return {
      identification: getPractitionerIdentification(practitioner, sensitiveDataAvailable),
      specialties: undefined,
   };
};

const standalonePractitionerSafePresenter: PractitionerSafePresenterRenderer = (renderProps) => {
   return (
      <StyledStandalonePractitionerSafePresenter
         {...appendTestableElementAttributes(renderProps.testId)}
         className={renderProps.className}
      >
         {renderProps.displayName ? (
            <StyledStandaloneDisplayNameText testId={'displayName'}>
               <Translate>{renderProps.displayName}</Translate>
            </StyledStandaloneDisplayNameText>
         ) : null}
         {renderProps.withIdentification && renderProps.identification ? (
            <StyledStandaloneIdentificationText testId={'identification'}>
               <Translate>{renderProps.identification.type}</Translate>
               {': '}
               <Translate>{renderProps.identification.value}</Translate>
            </StyledStandaloneIdentificationText>
         ) : null}
         {renderProps.withSpecialties ? (
            <TooltipText
               testId={'specialties'}
               lines={2}
               content={
                  <ul>
                     {renderProps.specialties?.map((specialty) => (
                        <li key={specialty}>{specialty}</li>
                     ))}
                  </ul>
               }
            >
               {renderProps.specialties ? (
                  <>{renderProps.specialties.join(', ')}</>
               ) : (
                  <Translate>{translationKeys.noSpecialties}</Translate>
               )}
            </TooltipText>
         ) : null}
      </StyledStandalonePractitionerSafePresenter>
   );
};

const textPractitionerSafePresenter: PractitionerSafePresenterRenderer = (renderProps) => {
   return (
      <span {...appendTestableElementAttributes(renderProps.testId)} className={renderProps.className}>
         {renderProps.displayName ? (
            <Text testId={'displayName'}>
               <Translate>{renderProps.displayName}</Translate>
            </Text>
         ) : null}
         {renderProps.withIdentification && renderProps.identification ? (
            <>
               {', '}
               <Text testId={'identification'}>
                  <Translate>{renderProps.identification.type}</Translate>
                  {': '}
                  <Translate>{renderProps.identification.value}</Translate>
               </Text>
            </>
         ) : null}
         {renderProps.withSpecialties ? (
            <>
               {', '}
               <Text testId={'specialties'}>
                  {renderProps.specialties ? (
                     <>{renderProps.specialties.join(', ')}</>
                  ) : (
                     <Translate>{translationKeys.noSpecialties}</Translate>
                  )}
               </Text>
            </>
         ) : null}
      </span>
   );
};

export const getPractitionerSafePresenterRenderer = (
   children: PractitionerSafePresenterRenderer | undefined,
   type: PractitionerSafePresenterType | undefined
): PractitionerSafePresenterRenderer => {
   if (children) {
      return children;
   }

   switch (type) {
      case 'standalone':
         return standalonePractitionerSafePresenter;
      case 'text':
         return textPractitionerSafePresenter;
      default:
         return assertUnreachable(type);
   }
};
